import React, { FC, useMemo, useState } from 'react';
import { FieldType } from 'src/entity/ProjectSettingsForm/types';
import TableService from 'src/shared/api/table/TableService';
import {
  BatchSubsetFilter,
  BatchSubsetFilterOperator,
  BatchSubsetOperation,
  BatchSubsetOperationRequest,
  BatchSubsetPreviewItem,
} from 'src/shared/api/table/types';
import { setError, setSuccess, setWarning } from 'src/shared/lib/utils';
import { Button, Modal } from 'src/shared/ui';

type BatchSubsetOperationModalProps = {
  tableId: number;
  formStructure: FieldType[];
  onClose: () => void;
  onApplied: () => void;
};

type FilterDraft = {
  id: number;
  column: string;
  operator: BatchSubsetFilterOperator;
  valuesInput: string;
  includeHierarchy: boolean;
};

const FILTER_OPERATOR_OPTIONS: Array<{ value: BatchSubsetFilterOperator; label: string }> = [
  { value: 'in', label: 'В списке' },
  { value: 'not_in', label: 'Не в списке' },
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'contains', label: 'Содержит' },
  { value: 'isnull', label: 'Пусто' },
  { value: 'notnull', label: 'Не пусто' },
];

const OPERATION_OPTIONS: Array<{ value: BatchSubsetOperation; label: string }> = [
  { value: 'set', label: 'Установить значение' },
  { value: 'increment', label: 'Увеличить (+)' },
  { value: 'multiply', label: 'Умножить (*)' },
  { value: 'clear', label: 'Очистить значение' },
];

const defaultFilterDraft = (id: number): FilterDraft => ({
  id,
  column: '',
  operator: 'in',
  valuesInput: '',
  includeHierarchy: false,
});

const splitValuesInput = (raw: string): string[] =>
  raw
    .split(/[,;\n]/)
    .map(value => value.trim())
    .filter(Boolean);

const parseBoolInput = (raw: string): boolean | null => {
  const normalized = raw.trim().toLowerCase();
  if (['true', '1', 'yes', 'y', 'да', 'д'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n', 'нет', 'н'].includes(normalized)) return false;
  return null;
};

const parsePrimitiveValue = (raw: string): string | number | boolean | null => {
  const normalized = raw.trim();
  if (!normalized) return '';

  const lower = normalized.toLowerCase();
  if (lower === 'null') return null;

  const boolValue = parseBoolInput(normalized);
  if (boolValue !== null) return boolValue;

  if (/^-?\d+(?:\.\d+)?$/.test(normalized)) {
    return Number(normalized);
  }

  return normalized;
};

const parseFilterValueByColumnType = (raw: string, columnType: FieldType['columnType']) => {
  const normalized = raw.trim();
  if (!normalized) {
    return '';
  }

  if (normalized.toLowerCase() === 'null') {
    return null;
  }

  if (columnType === 'number' || columnType === 'floatNumber' || columnType === 'reference') {
    const numberValue = Number(normalized);
    if (Number.isNaN(numberValue)) {
      throw new Error(`Значение "${normalized}" должно быть числом`);
    }
    return numberValue;
  }

  if (columnType === 'boolean') {
    const boolValue = parseBoolInput(normalized);
    if (boolValue === null) {
      throw new Error(`Значение "${normalized}" должно быть true/false (или да/нет)`);
    }
    return boolValue;
  }

  return parsePrimitiveValue(normalized);
};

const formatPreviewValue = (value: unknown): string => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_error) {
      return String(value);
    }
  }
  return String(value);
};

const operatorRequiresValue = (operator: BatchSubsetFilterOperator): boolean =>
  operator !== 'isnull' && operator !== 'notnull';

export const BatchSubsetOperationModal: FC<BatchSubsetOperationModalProps> = ({
  tableId,
  formStructure,
  onClose,
  onApplied,
}) => {
  const editableFields = useMemo(
    () =>
      formStructure.filter(
        field =>
          field.columnName &&
          field.columnName.toLowerCase() !== 'actions' &&
          field.columnName.toLowerCase() !== 'id',
      ),
    [formStructure],
  );
  const fieldsByColumn = useMemo(() => {
    const map = new Map<string, FieldType>();
    editableFields.forEach(field => {
      map.set(field.columnName, field);
    });
    return map;
  }, [editableFields]);

  const [filters, setFilters] = useState<FilterDraft[]>([defaultFilterDraft(1)]);
  const [targetColumn, setTargetColumn] = useState<string>(editableFields[0]?.columnName || '');
  const [operation, setOperation] = useState<BatchSubsetOperation>('set');
  const [valueInput, setValueInput] = useState<string>('');
  const [previewLimit, setPreviewLimit] = useState<string>('50');
  const [previewItems, setPreviewItems] = useState<BatchSubsetPreviewItem[]>([]);
  const [matchedCount, setMatchedCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  const canApplyWithoutValue = operation === 'clear';

  const updateFilter = (id: number, patch: Partial<FilterDraft>) => {
    setFilters(prev => prev.map(filter => (filter.id === id ? { ...filter, ...patch } : filter)));
  };

  const addFilter = () => {
    setFilters(prev => [...prev, defaultFilterDraft(Date.now())]);
  };

  const removeFilter = (id: number) => {
    setFilters(prev => (prev.length <= 1 ? prev : prev.filter(filter => filter.id !== id)));
  };

  const buildRequest = (): BatchSubsetOperationRequest => {
    const targetField = fieldsByColumn.get(targetColumn);
    if (!targetField) {
      throw new Error('Не выбрана целевая колонка');
    }

    const normalizedFilters: BatchSubsetFilter[] = filters
      .filter(filter => filter.column.trim())
      .map(filter => {
        const selectedField = fieldsByColumn.get(filter.column);
        if (!selectedField) {
          throw new Error(`Неизвестная колонка фильтра: ${filter.column}`);
        }

        let parsedValues: Array<string | number | boolean | null> = [];
        if (operatorRequiresValue(filter.operator)) {
          parsedValues = splitValuesInput(filter.valuesInput).map(rawValue =>
            parseFilterValueByColumnType(rawValue, selectedField.columnType),
          );
          if (!parsedValues.length) {
            throw new Error(
              `Для фильтра "${selectedField.columnLabel}" укажите хотя бы одно значение`,
            );
          }
        }

        const isReferenceField = Boolean(selectedField.referenceFormId);
        return {
          column: selectedField.columnName,
          operator: filter.operator,
          values: parsedValues,
          includeHierarchy: isReferenceField ? filter.includeHierarchy : false,
        };
      });

    if (!normalizedFilters.length) {
      throw new Error('Добавьте хотя бы один фильтр');
    }

    let operationValue: string | number | boolean | null | undefined;
    if (!canApplyWithoutValue) {
      const prepared = valueInput.trim();
      if (!prepared) {
        throw new Error('Укажите значение операции');
      }

      if (
        targetField.columnType === 'number' ||
        targetField.columnType === 'floatNumber' ||
        targetField.columnType === 'reference'
      ) {
        const numericValue = Number(prepared);
        if (Number.isNaN(numericValue)) {
          throw new Error('Значение операции должно быть числом');
        }
        operationValue = numericValue;
      } else if (targetField.columnType === 'boolean') {
        const boolValue = parseBoolInput(prepared);
        if (boolValue === null) {
          throw new Error('Значение операции должно быть true/false (или да/нет)');
        }
        operationValue = boolValue;
      } else {
        operationValue = prepared;
      }
    }

    const parsedPreviewLimit = Number(previewLimit.trim());
    const normalizedPreviewLimit =
      Number.isFinite(parsedPreviewLimit) && parsedPreviewLimit > 0
        ? Math.floor(parsedPreviewLimit)
        : undefined;

    return {
      filters: normalizedFilters,
      targetColumn: targetField.columnName,
      operation,
      value: operationValue,
      previewLimit: normalizedPreviewLimit,
    };
  };

  const handlePreview = async () => {
    let payload: BatchSubsetOperationRequest;
    try {
      payload = buildRequest();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Некорректные параметры массовой операции');
      return;
    }

    setPreviewLoading(true);
    try {
      const { data } = await TableService.batchSubsetPreview(tableId, payload);
      setMatchedCount(data.matchedCount);
      setPreviewItems(data.preview || []);
      setSuccess(`Предпросмотр загружен: найдено строк ${data.matchedCount}`);
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Не удалось загрузить предпросмотр');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    let payload: BatchSubsetOperationRequest;
    try {
      payload = buildRequest();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Некорректные параметры массовой операции');
      return;
    }

    setApplyLoading(true);
    try {
      const { data } = await TableService.batchSubsetApply(tableId, payload);
      setSuccess(`Обновлено строк: ${data.updatedCount} (подходящих: ${data.matchedCount})`);
      if (data.workflowWarnings?.length) {
        const uniqueWarnings = Array.from(new Set(data.workflowWarnings.filter(Boolean)));
        const preview = uniqueWarnings.slice(0, 3).join('; ');
        const extraCount = uniqueWarnings.length - Math.min(uniqueWarnings.length, 3);
        const suffix = extraCount > 0 ? ` (+${extraCount})` : '';
        setWarning(`Бизнес-процесс: ${preview}${suffix}`);
      }
      onApplied();
      onClose();
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Не удалось применить массовую операцию');
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="flex w-[72rem] max-w-[95dvw] flex-col">
        <div className="font-gothampro-500 mb-2 text-2xl text-green-100">
          Массовая операция по подмножеству
        </div>
        <div className="font-gothampro-400 mb-5 text-sm dark:text-white">
          Отфильтруйте строки, проверьте затронутые ячейки в предпросмотре и примените изменение к
          выбранной колонке.
        </div>

        <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs opacity-70 dark:text-white">Целевая колонка</label>
            <select
              className="h-11 rounded border border-violet-400 bg-grey-1200 px-3 text-sm dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
              value={targetColumn}
              onChange={event => setTargetColumn(event.target.value)}
            >
              {editableFields.map(field => (
                <option key={field.columnName} value={field.columnName}>
                  {field.columnLabel || field.columnName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs opacity-70 dark:text-white">Операция</label>
            <select
              className="h-11 rounded border border-violet-400 bg-grey-1200 px-3 text-sm dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
              value={operation}
              onChange={event => setOperation(event.target.value as BatchSubsetOperation)}
            >
              {OPERATION_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs opacity-70 dark:text-white">Значение операции</label>
            <input
              className="h-11 rounded border border-violet-400 bg-grey-1200 px-3 text-sm dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
              value={valueInput}
              onChange={event => setValueInput(event.target.value)}
              disabled={canApplyWithoutValue}
              placeholder={canApplyWithoutValue ? 'Не требуется для очистки' : 'Введите значение'}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs opacity-70 dark:text-white">Лимит предпросмотра</label>
            <input
              className="h-11 rounded border border-violet-400 bg-grey-1200 px-3 text-sm dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
              value={previewLimit}
              onChange={event => setPreviewLimit(event.target.value)}
              placeholder="50"
            />
          </div>
        </div>

        <div className="mb-3 flex items-center">
          <div className="font-gothampro-500 text-sm dark:text-white">Фильтры</div>
          <Button
            size="small"
            variant="violetOutline"
            classNames="ml-auto w-32"
            onClick={addFilter}
            disabled={previewLoading || applyLoading}
          >
            Добавить фильтр
          </Button>
        </div>

        <div className="max-h-[18rem] overflow-y-auto rounded border border-grey-1000 p-3">
          {filters.map(filter => {
            const selectedField = fieldsByColumn.get(filter.column);
            const isReferenceField = Boolean(selectedField?.referenceFormId);

            return (
              <div key={filter.id} className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-12">
                <div className="md:col-span-3">
                  <select
                    className="h-10 w-full rounded border border-violet-400 bg-grey-1200 px-3 text-sm dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                    value={filter.column}
                    onChange={event =>
                      updateFilter(filter.id, {
                        column: event.target.value,
                        includeHierarchy: false,
                      })
                    }
                  >
                    <option value="">Выберите колонку</option>
                    {editableFields.map(field => (
                      <option key={field.columnName} value={field.columnName}>
                        {field.columnLabel || field.columnName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <select
                    className="h-10 w-full rounded border border-violet-400 bg-grey-1200 px-3 text-sm dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                    value={filter.operator}
                    onChange={event =>
                      updateFilter(filter.id, {
                        operator: event.target.value as BatchSubsetFilterOperator,
                      })
                    }
                  >
                    {FILTER_OPERATOR_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-4">
                  <input
                    className="h-10 w-full rounded border border-violet-400 bg-grey-1200 px-3 text-sm dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                    value={filter.valuesInput}
                    onChange={event => updateFilter(filter.id, { valuesInput: event.target.value })}
                    disabled={!operatorRequiresValue(filter.operator)}
                    placeholder={
                      operatorRequiresValue(filter.operator)
                        ? 'Значения через запятую'
                        : 'Значения не требуются'
                    }
                  />
                </div>

                <div className="flex items-center md:col-span-2">
                  <label className="flex items-center gap-2 text-xs dark:text-white">
                    <input
                      type="checkbox"
                      checked={filter.includeHierarchy && isReferenceField}
                      disabled={!isReferenceField}
                      onChange={event =>
                        updateFilter(filter.id, {
                          includeHierarchy: event.target.checked,
                        })
                      }
                    />
                    Учитывать иерархию
                  </label>
                </div>

                <div className="flex items-center justify-end md:col-span-1">
                  <Button
                    size="small"
                    variant="greyLight"
                    classNames="w-20"
                    onClick={() => removeFilter(filter.id)}
                    disabled={filters.length <= 1 || previewLoading || applyLoading}
                  >
                    Удалить
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex gap-2">
          <Button
            size="small"
            variant="violet"
            classNames="w-32"
            onClick={handlePreview}
            disabled={previewLoading || applyLoading}
          >
            {previewLoading ? 'Загрузка...' : 'Предпросмотр'}
          </Button>
          <Button
            size="small"
            variant="green"
            classNames="w-32"
            onClick={handleApply}
            disabled={previewLoading || applyLoading}
          >
            {applyLoading ? 'Применение...' : 'Применить'}
          </Button>
          <Button
            size="small"
            variant="greyLight"
            classNames="ml-auto w-28"
            onClick={onClose}
            disabled={previewLoading || applyLoading}
          >
            Закрыть
          </Button>
        </div>

        <div className="mt-4 rounded border border-grey-1000 p-3">
          <div className="text-sm dark:text-white">
            Подходящих строк: <span className="font-gothampro-500">{matchedCount ?? '-'}</span>
          </div>
          <div className="mt-2 max-h-[14rem] overflow-y-auto rounded border border-grey-1000 p-2">
            {!previewItems.length && (
              <div className="text-xs opacity-70 dark:text-white">Предпросмотр пуст</div>
            )}
            {previewItems.map(item => (
              <div key={item.id} className="mb-1 text-xs dark:text-white">
                ID {item.id}: {formatPreviewValue(item.value)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};
